/**
 * Product Upsell Handlers
 * Global functions for handling product upsell add to cart and variant selection
 */

// Handle variant selection change
window.handleProductUpsellVariantChange = function (selectElement, formId) {
  // Derive the form from the element itself: form ids were historically
  // non-unique (Liquid drops filters inside {% render %} arguments), so an
  // id lookup could return ANOTHER card's form. closest() is collision-proof;
  // the id lookup remains only as a legacy fallback.
  const form = selectElement.closest("form.upsell-form") || document.getElementById(formId);
  if (!form) {
    console.error("Form not found:", formId);
    return;
  }

  // The product JSON script is rendered INSIDE the form — never pair by id.
  const productJsonEl =
    form.querySelector('script[type="application/json"]') || document.getElementById("ProductJson-" + formId);
  if (!productJsonEl) {
    console.error("Product JSON not found for form:", formId);
    return;
  }

  try {
    const product = JSON.parse(productJsonEl.textContent);
    const selects = form.querySelectorAll('select[name^="options["]');
    const selectedOptions = Array.from(selects).map((s) => s.value);

    // Find matching variant
    const variant = product.variants.find((v) => selectedOptions.every((opt, i) => v.options[i] === opt));

    if (variant) {
      const hiddenInput = form.querySelector('input[name="id"]');
      if (hiddenInput) {
        hiddenInput.value = variant.id;
      }

      // Update price if displayed
      const priceElement = form.querySelector(".upsell-price");
      if (priceElement && variant.price) {
        const currency = document.querySelector('meta[name="currency"]')?.content || "USD";
        const formattedPrice = formatMoney(variant.price, currency);
        priceElement.textContent = formattedPrice;
      }
    }
  } catch (error) {
    console.error("Error handling variant change:", error);
  }
};

// Handle add to cart
window.handleProductUpsellAdd = async function (buttonElement, formId) {
  // Same collision-proofing as the variant handler: the clicked button's own
  // form is authoritative; the id parameter is only a legacy fallback.
  const form = buttonElement.closest("form.upsell-form") || document.getElementById(formId);
  if (!form) {
    console.error("Form not found:", formId);
    return;
  }

  const variantId = form.querySelector('input[name="id"]')?.value;
  if (!variantId) {
    console.error("No variant ID found");
    return;
  }

  // Quantity comes from the block. Automatic quantity discounts (Kaching
  // bundles) are applied server-side on cart quantity, so adding 3 here is all
  // that is needed to get the bundle price — no discount code, no properties.
  const qty = parseInt(form.querySelector('input[name="quantity"]')?.value, 10) || 1;

  // Disable button and show loading state
  const originalHTML = buttonElement.innerHTML;
  buttonElement.disabled = true;
  buttonElement.classList.add("is-loading");
  buttonElement.innerHTML = '<span class="button-spinner"></span>';

  try {
    // Add to cart
    const response = await fetch(window.routes.cart_add_url + ".js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({
        items: [{ id: variantId, quantity: qty }],
      }),
    });

    if (!response.ok) {
      throw new Error("Failed to add to cart");
    }

    // Try to parse as JSON, handle errors gracefully
    let data;
    try {
      const text = await response.text();
      data = JSON.parse(text);
    } catch (parseError) {
      console.error("Failed to parse cart response:", parseError);
      throw new Error("Invalid cart response");
    }

    // Show success state
    buttonElement.classList.remove("is-loading");
    buttonElement.classList.add("upsell-add-success");
    const successText = buttonElement.dataset.successText || "Added!";
    buttonElement.innerHTML = "✓ " + successText;

    // Reset button after 2 seconds
    setTimeout(() => {
      buttonElement.classList.remove("upsell-add-success");
      buttonElement.innerHTML = originalHTML;
      buttonElement.disabled = false;
    }, 2000);

    // Update cart count bubble
    fetch("/cart.js")
      .then((r) => r.json())
      .then((cart) => {
        document.querySelectorAll(".cart-count-bubble, [data-cart-count]").forEach((el) => {
          el.textContent = cart.item_count;
        });
      })
      .catch(() => {});

    // Refresh cart drawer
    const cartDrawer = document.querySelector("cart-drawer");
    if (cartDrawer) {
      fetch(`${window.routes.cart_url}?section_id=cart-drawer`)
        .then((r) => r.text())
        .then((html) => {
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");
          const newCartDrawer = doc.querySelector("cart-drawer");
          if (newCartDrawer) {
            cartDrawer.innerHTML = newCartDrawer.innerHTML;
          }
        })
        .catch(() => {});
    }
  } catch (error) {
    console.error("Error adding to cart:", error);

    // Show error state
    buttonElement.classList.remove("is-loading");
    buttonElement.classList.add("upsell-add-error");
    buttonElement.innerHTML = "✗ Error";

    // Reset button after 2 seconds
    setTimeout(() => {
      buttonElement.classList.remove("upsell-add-error");
      buttonElement.innerHTML = originalHTML;
      buttonElement.disabled = false;
    }, 2000);
  }
};

// Helper function to format money
function formatMoney(cents, currency = "USD") {
  const amount = cents / 100;

  // Try to use Shopify's money format if available
  if (typeof Shopify !== "undefined" && Shopify.formatMoney) {
    return Shopify.formatMoney(cents, Shopify.money_format || "{{amount}}");
  }

  // Fallback formatting
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency,
  }).format(amount);
}

/**
 * ------------------------------------------------------------------
 * Live upsell deal prices from the Kaching Bundles page embed.
 * ------------------------------------------------------------------
 * The cart drawer's upsell cards render the plain variant price via Liquid,
 * which cannot see Kaching's function-based automatic discounts. Kaching
 * embeds its deal configuration in each product page as:
 *
 *   <script class="kaching-bundles-deal-block-settings"
 *           data-product-id="123" type="application/json">{...}</script>
 *
 * with one block per market AND per A/B-test arm, each containing
 * { currency: { currencyCode }, abTestVariantId, dealBars: [
 *     { dealBarType: "quantity-break", quantity: 1,
 *       discountType: "specific" | "percentage", discountValue: N }, ... ] }
 *
 * On drawer open we fetch each upsell's product page (same-origin, so the
 * shopper's own market/session applies), extract the single-quantity deal
 * price for the active presentment currency, and update the card's price +
 * strikethrough.
 *
 * IMPORTANT SAFETY RULES (this code must never under-promise a price):
 * - When the product/currency has MULTIPLE A/B arms with different qty-1
 *   prices, we display the MOST EXPENSIVE arm's price: a shopper in the
 *   cheaper arm gets a pleasant surprise in the cart; a shopper in the
 *   expensive arm sees exactly what they will pay.
 * - If any arm cannot be evaluated (unknown discount type / bad value) we
 *   cannot bound the maximum, so we keep the server-rendered price.
 * - Any parse failure, missing embed, empty embed (product without a deal),
 *   or implausible chosen price (<= 0 or >= list price — e.g. the priciest
 *   arm has no real discount) also returns null.
 * - This intentionally depends on Kaching's embed format; if Kaching ever
 *   changes it, every failure path degrades silently to the Liquid price.
 */
(function () {
  "use strict";

  var DEAL_CACHE_TTL = 10 * 60 * 1000; // ~10 minutes
  var dealFetchInFlight = {};

  function getPresentmentCurrency() {
    try {
      if (window.Shopify && Shopify.currency && Shopify.currency.active) {
        return Shopify.currency.active;
      }
    } catch (e) {
      /* noop */
    }
    var meta = document.querySelector('meta[name="currency"]');
    return meta ? meta.content : null;
  }

  function formatDealMoney(amount, currencyCode) {
    try {
      if (typeof Shopify !== "undefined" && Shopify.formatMoney && Shopify.money_format) {
        return Shopify.formatMoney(Math.round(amount * 100), Shopify.money_format);
      }
    } catch (e) {
      /* fall through */
    }
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "en", {
        style: "currency",
        currency: currencyCode || "USD",
      }).format(amount);
    } catch (e) {
      return amount.toFixed(2);
    }
  }

  /**
   * Parse the qty-1 Kaching deal price for a product out of its page HTML.
   * Returns a number (major units) or null when no trustworthy price exists.
   */
  function parseKachingDealPrice(html, productId, currencyCode, listPrice) {
    try {
      if (!html || !productId || !currencyCode || !(listPrice > 0)) return null;

      var armPrices = {}; // abTestVariantId -> price
      var re = /<script([^>]*class="kaching-bundles-deal-block-settings"[^>]*)>([\s\S]*?)<\/script>/g;
      var m;
      while ((m = re.exec(html)) !== null) {
        var attrs = m[1];
        var body = (m[2] || "").trim();
        if (!body) continue; // product has the app block but no active deal

        var pidMatch = attrs.match(/data-product-id="(\d+)"/);
        if (!pidMatch || pidMatch[1] !== String(productId)) continue;

        var cfg;
        try {
          cfg = JSON.parse(body);
        } catch (e) {
          continue;
        }
        if (!cfg || !cfg.currency || cfg.currency.currencyCode !== currencyCode) continue;
        if (!Array.isArray(cfg.dealBars)) continue;

        var bar = null;
        for (var i = 0; i < cfg.dealBars.length; i++) {
          var b = cfg.dealBars[i];
          if (b && b.dealBarType === "quantity-break" && b.quantity === 1) {
            bar = b;
            break;
          }
        }
        if (!bar) continue;

        var price = null;
        var value = Number(bar.discountValue);
        if (isFinite(value)) {
          if (bar.discountType === "specific") {
            price = value; // fixed deal price in this market's currency
          } else if (bar.discountType === "percentage") {
            price = listPrice * (1 - value / 100);
          }
          // unknown discount type leaves price = null (unevaluable arm)
        }
        // Record EVERY arm, evaluable or not: an unevaluable arm (null)
        // means we cannot bound the maximum and must fall back.
        armPrices[cfg.abTestVariantId || "default"] =
          price == null ? null : Math.round(price * 100) / 100;
      }

      // Never under-promise: across A/B arms, display the MOST EXPENSIVE
      // arm's qty-1 price. If any arm is unevaluable the maximum is
      // unknowable -> fall back.
      var prices = Object.keys(armPrices).map(function (k) {
        return armPrices[k];
      });
      if (prices.length === 0) return null;
      var maxPrice = prices[0];
      for (var j = 0; j < prices.length; j++) {
        if (prices[j] == null) return null;
        if (prices[j] > maxPrice) maxPrice = prices[j];
      }

      // Plausibility of the CHOSEN price: must be a real discount below the
      // list price (e.g. if the priciest arm's "deal" equals list, there is
      // no discount to show).
      if (!(maxPrice > 0) || !(maxPrice < listPrice - 0.001)) return null;
      return maxPrice;
    } catch (e) {
      return null;
    }
  }

  function applyDealPriceToForm(form, price, currencyCode) {
    if (price == null) return;
    var listPrice = parseFloat(form.dataset.variantPrice);
    var comparePrice = parseFloat(form.dataset.comparePrice) || 0;
    var strike = Math.max(comparePrice, listPrice);

    var priceEl = form.querySelector(".upsell-price");
    if (priceEl) priceEl.textContent = formatDealMoney(price, currencyCode);

    var cmpEl = form.querySelector(".upsell-compare-price");
    if (cmpEl && strike > price + 0.005) {
      cmpEl.textContent = formatDealMoney(strike, currencyCode);
      cmpEl.style.display = "";
    }
  }

  function readDealCache(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return undefined;
      var entry = JSON.parse(raw);
      if (!entry || Date.now() - entry.t > DEAL_CACHE_TTL) return undefined;
      return entry.p; // number or null ("no trustworthy deal") — both cached
    } catch (e) {
      return undefined;
    }
  }

  function writeDealCache(key, price) {
    try {
      sessionStorage.setItem(key, JSON.stringify({ p: price, t: Date.now() }));
    } catch (e) {
      /* storage full/blocked: fine, we just refetch next time */
    }
  }

  function refreshUpsellDealPrices() {
    var forms = document.querySelectorAll(".upsell-form[data-product-handle]");
    if (!forms.length) return;
    var currency = getPresentmentCurrency();
    if (!currency) return;

    forms.forEach(function (form) {
      var handle = form.dataset.productHandle;
      var productId = form.dataset.productId;
      var listPrice = parseFloat(form.dataset.variantPrice);
      if (!handle || !productId || !(listPrice > 0)) return;

      // v2: cache key bumped when the A/B policy changed to max-arm, so stale
      // nulls cached by the previous build cannot mask the new behavior.
      var cacheKey = "kaching_deal_v2_" + productId + "_" + currency;
      var cached = readDealCache(cacheKey);
      if (cached !== undefined) {
        applyDealPriceToForm(form, cached, currency);
        return;
      }
      if (dealFetchInFlight[cacheKey]) return;
      dealFetchInFlight[cacheKey] = true;

      // ?view=kaching-deal = templates/product.kaching-deal.liquid on layout/kaching-deal.liquid:
      // only <head> + app embeds (~40 KB on the wire instead of ~150-180 KB), and the same
      // kaching-bundles-deal-block-settings JSON, every market and every A/B arm. If that
      // template is ever removed, Shopify renders the normal product page and this still works.
      fetch("/products/" + handle + "?view=kaching-deal", { credentials: "same-origin" })
        .then(function (r) {
          return r.ok ? r.text() : null;
        })
        .then(function (html) {
          var price = html ? parseKachingDealPrice(html, productId, currency, listPrice) : null;
          writeDealCache(cacheKey, price);
          // Re-query: the drawer may have re-rendered while we fetched.
          document
            .querySelectorAll('.upsell-form[data-product-id="' + productId + '"]')
            .forEach(function (f) {
              applyDealPriceToForm(f, price, currency);
            });
        })
        .catch(function () {
          /* network error: keep the Liquid-rendered price */
        })
        .finally(function () {
          delete dealFetchInFlight[cacheKey];
        });
    });
  }

  // Run on load and after every drawer re-render (innerHTML swaps restore the
  // Liquid prices, so we re-apply from cache). Debounced: the observer fires
  // for every drawer mutation, including the countdown timer's ticks.
  var dealRefreshTimer = null;
  function scheduleDealRefresh() {
    if (dealRefreshTimer) clearTimeout(dealRefreshTimer);
    dealRefreshTimer = setTimeout(refreshUpsellDealPrices, 250);
  }

  // First run waits for an idle moment: on page load the drawer is closed, so the deal
  // fetches must not compete with the page's own requests. Drawer re-renders (add to cart)
  // still refresh after 250 ms via the observer below.
  function scheduleDealRefreshWhenIdle() {
    if (window.requestIdleCallback) requestIdleCallback(scheduleDealRefresh, { timeout: 3000 });
    else setTimeout(scheduleDealRefresh, 1500);
  }
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", scheduleDealRefreshWhenIdle);
  } else {
    scheduleDealRefreshWhenIdle();
  }

  if (!window.__upsellDealPriceObserverInstalled) {
    window.__upsellDealPriceObserverInstalled = true;
    var observeTarget = document.querySelector("cart-drawer") || document.body;
    new MutationObserver(function (mutations) {
      // Ignore pure text ticks (e.g. the countdown timer updating a span).
      for (var i = 0; i < mutations.length; i++) {
        var mut = mutations[i];
        for (var j = 0; j < mut.addedNodes.length; j++) {
          if (mut.addedNodes[j].nodeType === 1) {
            scheduleDealRefresh();
            return;
          }
        }
      }
    }).observe(observeTarget, { childList: true, subtree: true });
  }
})();
